import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslations } from '../locales/useI18n';
import { colors, spacing, borderRadius } from '../styles/theme';
import EventFinderAPI from '../services/api';
import { SystemInsights } from '../types/api';
import SEO, { createBreadcrumbStructuredData } from '../components/SEO';

const PageContainer = styled.div`
  min-height: calc(100vh - 200px);
  padding: ${spacing[8]} ${spacing[4]};
  background: linear-gradient(135deg, ${colors.background.primary} 0%, ${colors.background.secondary} 100%);
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.text.primary};
  text-align: center;
  margin-bottom: ${spacing[8]};
  background: linear-gradient(135deg, ${colors.primary[600]}, ${colors.secondary[500]});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${spacing[6]};
  margin-bottom: ${spacing[8]};
`;

const StatCard = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  padding: ${spacing[6]};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid ${colors.border.light};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.primary[600]};
  margin-bottom: ${spacing[2]};
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: ${spacing[6]};
  padding-bottom: ${spacing[3]};
  border-bottom: 2px solid ${colors.primary[200]};
`;

const ListContainer = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  padding: ${spacing[6]};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid ${colors.border.light};
  margin-bottom: ${spacing[8]};
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing[3]} 0;
  border-bottom: 1px solid ${colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const ItemLabel = styled.span`
  color: ${colors.text.primary};
  font-weight: 500;
`;

const ItemValue = styled.span`
  color: ${colors.primary[600]};
  font-weight: 600;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.125rem;
  color: ${colors.text.secondary};
`;

const ErrorState = styled.div`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  border-radius: ${borderRadius.md};
  padding: ${spacing[6]};
  text-align: center;
  color: ${colors.error[700]};
  margin: ${spacing[8]} 0;
`;

export const StatisticsPage: React.FC = () => {
  const { t } = useTranslations();
  const [data, setData] = useState<SystemInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await EventFinderAPI.getSystemInsights(undefined, undefined, '30');
        setData(data);
      } catch (err: any) {
        console.error('Failed to fetch system insights:', err);
        setError(err.message || 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentContainer>
          <LoadingState>Loading statistics...</LoadingState>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ContentContainer>
          <PageTitle>{t('footer.siteStatistics')}</PageTitle>
          <ErrorState>
            <h3>Error Loading Statistics</h3>
            <p>{error}</p>
          </ErrorState>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (!data) {
    return (
      <PageContainer>
        <ContentContainer>
          <PageTitle>{t('footer.siteStatistics')}</PageTitle>
          <ErrorState>
            <h3>No Data Available</h3>
            <p>Statistics data is currently unavailable.</p>
          </ErrorState>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <SEO
        title="Event Statistics - Platform Insights & Data | Event Finder"
        description="View Event Finder platform statistics including total events, top cities, event type distribution, and system insights. Explore our growing database of worldwide events."
        keywords="event statistics, event data, event insights, event analytics, top event cities, event types, event platform data"
        canonical="https://www.eventfinder.site/statistics"
        structuredData={[
          createBreadcrumbStructuredData([
            { name: 'Home', url: 'https://www.eventfinder.site/' },
            { name: 'Statistics', url: 'https://www.eventfinder.site/statistics' }
          ])
        ]}
      />
      <ContentContainer>
        <PageTitle>{t('footer.siteStatistics')}</PageTitle>

        {/* Database Stats */}
        <SectionTitle>Database Statistics</SectionTitle>
        <StatsGrid>
          <StatCard>
            <StatValue>{formatNumber(data.systemStats.db.eventInfo.total)}</StatValue>
            <StatLabel>Total Events</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatNumber(data.systemStats.db.eventInfo.added.last30Days)}</StatValue>
            <StatLabel>Events Added (Last 30 Days)</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatNumber(data.systemStats.db.eventInfoMap.total)}</StatValue>
            <StatLabel>Event Locations</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatNumber(data.systemStats.search.documentCount)}</StatValue>
            <StatLabel>Search Documents</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* Event Insights */}
        <SectionTitle>Event Insights</SectionTitle>
        <StatsGrid>
          <StatCard>
            <StatValue>{formatNumber(data.insights.events.upcoming)}</StatValue>
            <StatLabel>Upcoming Events</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatNumber(data.insights.events.virtual)}</StatValue>
            <StatLabel>Virtual Events</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatPercentage(data.insights.events.virtualPercentage)}</StatValue>
            <StatLabel>Virtual Event Percentage</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* Top Cities */}
        <SectionTitle>Top Cities by Event Count</SectionTitle>
        <ListContainer>
          {data.insights.locations.topCities.map((city, index) => (
            <ListItem key={index}>
              <ItemLabel>{city.city}</ItemLabel>
              <ItemValue>{formatNumber(city.eventCount)} events</ItemValue>
            </ListItem>
          ))}
        </ListContainer>

        {/* Event Types Distribution */}
        <SectionTitle>Event Types Distribution</SectionTitle>
        <ListContainer>
          {data.insights.eventTypes.distribution.map((type, index) => (
            <ListItem key={index}>
              <ItemLabel>{type.type}</ItemLabel>
              <ItemValue>{formatNumber(type.eventCount)} events</ItemValue>
            </ListItem>
          ))}
        </ListContainer>

        {/* System Information */}
        <SectionTitle>System Information</SectionTitle>
        <StatsGrid>
          <StatCard>
            <StatValue>{data.timeframe} days</StatValue>
            <StatLabel>Data Timeframe</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{(data.systemStats.search.storageSize / (1024 * 1024)).toFixed(1)} MB</StatValue>
            <StatLabel>Search Index Size</StatLabel>
          </StatCard>
        </StatsGrid>
      </ContentContainer>
    </PageContainer>
  );
};

export default StatisticsPage;